import {defineStore} from 'pinia'
import {ref} from 'vue'
import {useRouter} from 'vue-router'
import {App} from '@capacitor/app'
import {useMessages} from '@toolify/client/src/modules/notifications/composables/useMessages'
import {IToJson} from '@toolify/client/src/types/IToJson'
import {mapDeploymentVersionResponseInstance} from '@toolify/client/src/modules/api/singletons/mapDeploymentVersionResponseInstance'
import {IDeploymentVersionResponseInstance} from '@toolify/server/src/adapters/InstanceApiResponseAdapter/types/responseInstances/IDeploymentVersionResponseInstance'
import {MessageActionKindType} from '@toolify/client/src/modules/notifications/enum/MessageActionKindType'
import {i18n} from '@toolify/client/src/setup/i18n'
import {GlobalSocketEventType} from '@toolify/server/src/services/SocketService/enum/GlobalSocketEventType'
import {useSocketStore} from '../SocketStore/useSocketStore'
import {useDeploymentVersionApiStore} from '@toolify/client/src/stores/api/DeploymentVersionApiStore/useDeploymentVersionApiStore'
import {useCurrentAppPlatform} from '@toolify/client/src/modules/platform/composables/useCurrentAppPlatform'
import {AppPlatformType} from '@toolify/server/src/models/mongoose/SessionModel/enum/AppPlatformType'
import {RouteName} from '@toolify/client/src/setup/router/enum/RouteName'
import {useAppUpdateStore} from '@toolify/client/src/stores/AppUpdateStore/useAppUpdateStore'

const FORCED_APP_RELOAD_MIN_DELAY_MS = 10 * 60 * 1000
const FORCED_APP_RELOAD_SPREAD_MS = 10 * 60 * 1000

export const useDeploymentVersionStore = defineStore('deploymentVersion', () => {
  const messages = useMessages()
  const socketStore = useSocketStore()
  const deploymentVersionApiStore = useDeploymentVersionApiStore()
  const router = useRouter()
  const {currentAppPlatformType} = useCurrentAppPlatform()

  const appUpdateTimeout = ref<number|null>(null)
  const latestDeploymentVersion = ref<IDeploymentVersionResponseInstance | null>(null)
  const runningDeploymentVersion = ref<IDeploymentVersionResponseInstance | null>(null)
  const runningVersion = ref(ENV.Version)
  const mustUpdate = ref(false)
  const isLatestLoaded = ref(false)

  function applyLatestDerivedState() {
    const deprecatedAt = runningDeploymentVersion.value?.deprecatedAt
    const required = !!deprecatedAt && new Date(deprecatedAt).getTime() <= Date.now()
    mustUpdate.value = required
    if(required && currentAppPlatformType === AppPlatformType.Desktop) {
      const appUpdateStore = useAppUpdateStore()
      void appUpdateStore.checkForUpdates()
    }
  }

  async function resolveRunningVersion(): Promise<void> {
    if(currentAppPlatformType === AppPlatformType.Mobile) {
      try {
        const appInfo = await App.getInfo()
        runningVersion.value = appInfo.version
        return
      } catch {
        runningVersion.value = ENV.Version
        return
      }
    }
    runningVersion.value = ENV.Version
  }

  async function loadLatestDeploymentVersion(): Promise<void> {
    const latestResponse = await deploymentVersionApiStore.getLatestDeploymentVersion(
      currentAppPlatformType,
      runningVersion.value,
    )
    latestDeploymentVersion.value = latestResponse.deploymentVersion
    runningDeploymentVersion.value = latestResponse.runningDeploymentVersion
    isLatestLoaded.value = true
    applyLatestDerivedState()
  }

  function redirectIfMustUpdate() {
    if(!mustUpdate.value) {
      return
    }
    if(router.currentRoute.value.name !== RouteName.Changelog) {
      void router.replace({name: RouteName.Changelog})
    }
  }

  function maybeReplaceLatest(deploymentVersion: IDeploymentVersionResponseInstance) {
    const currentLatest = latestDeploymentVersion.value
    if(currentLatest && !isLaterPublishedAt(deploymentVersion.publishedAt, currentLatest.publishedAt)) {
      return false
    }
    latestDeploymentVersion.value = deploymentVersion
    if(deploymentVersion.versionId === runningVersion.value) {
      runningDeploymentVersion.value = deploymentVersion
    }
    applyLatestDerivedState()
    return true
  }

  function onDeploymentVersionPublished(payload: IToJson<IDeploymentVersionResponseInstance>) {
    const deploymentVersion = mapDeploymentVersionResponseInstance(payload)
    if(deploymentVersion.appPlatformType !== currentAppPlatformType) {
      return
    }
    if(!maybeReplaceLatest(deploymentVersion)) {
      return
    }
    handleNewerPublishedVersion()
  }

  function handleNewerPublishedVersion() {
    if(mustUpdate.value) {
      redirectIfMustUpdate()
      return
    }
    const latest = latestDeploymentVersion.value
    if(!latest || latest.versionId === runningVersion.value) {
      return
    }
    if(currentAppPlatformType === AppPlatformType.Desktop) {
      return
    }
    const clearAppUpdateTimeout = () => {
      if(appUpdateTimeout.value) {
        clearTimeout(appUpdateTimeout.value)
        appUpdateTimeout.value = null
      }
    }
    const refresh = () => {
      clearAppUpdateTimeout()
      window.location.reload()
    }
    // stays until the user reacts; a later version replaces the card instead of stacking a second one
    messages.info(i18n.global.t('appUpdate.messages.refreshToUpdate', {appName: ENV.AppBrandingName}), {
      title: i18n.global.t('appUpdate.newVersionAvailable'),
      duration: 0,
      pinned: true,
      replaceKey: 'deployment.version',
      actions: [{
        label: i18n.global.t('notifications.messages.common.refreshPage'),
        kind: MessageActionKindType.Primary,
        onClick: refresh,
      }, {
        label: i18n.global.t('notifications.messages.common.later'),
        kind: MessageActionKindType.Secondary,
        // the user chose to refresh later, so no forced reload in 10 minutes
        onClick: clearAppUpdateTimeout,
      }],
    })
    // a newer version replaces the card, so it also replaces the pending reload
    clearAppUpdateTimeout()
    appUpdateTimeout.value = setTimeout(
      refresh,
      FORCED_APP_RELOAD_MIN_DELAY_MS + Math.random() * FORCED_APP_RELOAD_SPREAD_MS,
    ) as unknown as number
  }

  async function initialize() {
    socketStore.socket.on(GlobalSocketEventType.DeploymentVersionPublished, onDeploymentVersionPublished)
    await resolveRunningVersion()
    try {
      await loadLatestDeploymentVersion()
    } catch {
      return
    }
    redirectIfMustUpdate()
  }

  function dispose() {
    socketStore.socket.off(GlobalSocketEventType.DeploymentVersionPublished, onDeploymentVersionPublished)
  }

  return {
    latestDeploymentVersion,
    runningVersion,
    mustUpdate,
    isLatestLoaded,
    loadLatestDeploymentVersion,
    initialize,
    dispose,
  }
})

function isLaterPublishedAt(left: string | null | undefined, right: string | null | undefined): boolean {
  const leftTime = Date.parse(left || '')
  const rightTime = Date.parse(right || '')
  if(Number.isNaN(leftTime)) {
    return false
  }
  if(Number.isNaN(rightTime)) {
    return true
  }
  return leftTime > rightTime
}
